import { createFileRoute, Link } from "@tanstack/react-router";
import { readSelectedPlanId } from "@/lib/selected-plan";
import { trackExplore, hasExploreJourney } from "@/lib/explore-analytics";
import { trackFunnel } from "@/lib/funnel";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  getMyMembership,
  syncFoundingCheckoutSession,
} from "@/lib/membership.functions";
import { refreshMembership } from "@/lib/membership-refresh";
import {
  describeVerifiedPlan,
  type VerifiedPlan,
  type VerifiedPlanRow,
} from "@/lib/verified-plan";

import { Crown, Check, Loader2, ArrowRight, Mail } from "lucide-react";
import { z } from "zod";
import { DigitalConsentSummary } from "@/components/membership/DigitalConsentSummary";

export const Route = createFileRoute("/checkout/success")({
  validateSearch: (search: Record<string, unknown>) =>
    z
      .object({ session_id: z.string().optional() })
      .parse(search),
  head: () => ({
    meta: [
      { title: "Payment Successful — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Your Membership is confirmed. Welcome to Recovery Trauma™.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: CheckoutSuccessPage,
});

type Status = "pending" | "active" | "timeout";

function CheckoutSuccessPage() {
  const { session_id: sessionId } = Route.useSearch();
  const fetchMembership = useServerFn(getMyMembership);
  const syncCheckoutSession = useServerFn(syncFoundingCheckoutSession);
  const [status, setStatus] = useState<Status>("pending");
  const [email, setEmail] = useState<string | null>(null);
  const stopped = useRef(false);

  // Close the /explore funnel loop: payment landed, then activation confirmed.
  const [plan, setPlan] = useState<VerifiedPlan | null>(null);
  const paymentTracked = useRef(false);

  // Funnel step 5 — payment landed. De-duped per checkout session so polling,
  // re-renders and back-navigation can never double-count a conversion.
  useEffect(() => {
    trackFunnel(
      "payment_completed",
      {
        source: "checkout_success",
        provider: "stripe",
        plan: readSelectedPlanId() ?? undefined,
        dedupeKey: sessionId ?? "stripe_no_session",
      },
      { session_id: sessionId ?? null },
    );
  }, [sessionId]);

  useEffect(() => {
    if (paymentTracked.current || !hasExploreJourney()) return;
    paymentTracked.current = true;
    trackExplore("explore_payment_completed", { provider: "stripe" });
  }, []);

  useEffect(() => {
    if (status !== "active" || !hasExploreJourney()) return;
    trackExplore("explore_membership_activated", { provider: "stripe" });
  }, [status]);

  // Verify against the webhook-written membership row every 1.5s for ~30s.
  // The Stripe webhook is the source of truth; syncCheckoutSession is the
  // fallback that reads the session live if the webhook is slow or dropped.
  useEffect(() => {
    let tries = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const readVerified = async (): Promise<boolean> => {
      const m = (await fetchMembership()) as VerifiedPlanRow | null;
      const described = describeVerifiedPlan(m);
      if (m?.email) setEmail(m.email);
      if (described) setPlan(described);
      if (described?.isActive || m?.status === "active" || m?.status === "trialing") {
        refreshMembership();
        setStatus("active");
        return true;
      }
      return false;
    };

    const tick = async () => {
      if (stopped.current) return;
      tries += 1;
      try {
        if (sessionId && (tries === 1 || tries >= 3)) {
          const synced = (await syncCheckoutSession({
            data: { session_id: sessionId },
          })) as { status?: string; email?: string | null } | null;
          if (synced?.email) setEmail(synced.email);
          if (synced?.status === "active") {
            // Re-read so the plan price/renewal date come from the verified row.
            await readVerified();
            refreshMembership();
            setStatus("active");
            return;
          }
        }

        if (await readVerified()) return;
      } catch {
        /* keep polling */
      }
      if (tries >= 20) {
        setStatus("timeout");
        return;
      }
      timer = setTimeout(tick, 1500);
    };
    tick();
    return () => {
      stopped.current = true;
      if (timer) clearTimeout(timer);
    };
  }, [fetchMembership, sessionId, syncCheckoutSession]);


  return (
    <div className="min-h-[100svh] bg-gradient-to-b from-background via-background to-[var(--gold-soft)]/25 px-4 py-16 sm:py-24">
      <div className="mx-auto flex max-w-[720px] items-center justify-center">
        <div className="w-full rounded-[2.25rem] border border-gold/40 bg-gradient-to-b from-[var(--gold-soft)]/50 via-card/95 to-card/90 px-6 py-10 text-center shadow-[0_30px_80px_-30px_color-mix(in_oklab,var(--gold-deep)_45%,transparent)] backdrop-blur sm:px-12 sm:py-14">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[var(--gradient-gold)] shadow-[var(--shadow-gold)]">
            {status === "active" ? (
              <Check className="h-8 w-8 text-foreground" strokeWidth={2.5} />
            ) : status === "timeout" ? (
              <Crown className="h-7 w-7 text-foreground" strokeWidth={2.25} />
            ) : (
              <Loader2 className="h-7 w-7 animate-spin text-foreground" />
            )}
          </div>

          <p className="mt-5 inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/40 bg-background/70 px-3.5 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-[var(--gold-deep)]">
            Member
          </p>

          {status === "active" && (
            <>
              <h1 className="mt-5 font-display text-3xl leading-tight text-foreground sm:text-[38px]">
                You're in. Welcome to Recovery Trauma™.
              </h1>
              <p className="mx-auto mt-4 max-w-lg text-[16px] leading-relaxed text-muted-foreground sm:text-[17px]">
                Your payment is confirmed and your full access is unlocked
                now — nothing more to do.
              </p>
              <ul className="mx-auto mt-7 flex max-w-md flex-col gap-3 text-start text-[15px] leading-relaxed text-foreground/85">
                <li className="flex items-start gap-3">
                  <Bullet />{" "}
                  {plan?.isFounding ? "Founding rate: " : "Your plan: "}
                  <span className="font-medium text-foreground">
                    {plan?.priceLabel ?? "$14.99 USD/month"}
                  </span>{" "}
                  · monthly subscription · cancel anytime.
                </li>
                <li className="flex items-start gap-3">
                  <Bullet />{" "}
                  {plan?.cancelAtPeriodEnd
                    ? "Your access runs until"
                    : "Renews automatically on"}{" "}
                  <span className="font-medium text-foreground">
                    {plan?.nextChargeLabel ?? "your next billing date"}
                  </span>
                  .
                </li>
                <li className="flex items-start gap-3">
                  <Bullet /> Receipt sent to{" "}
                  <span className="font-medium text-foreground">{email ?? "your email"}</span>.
                </li>
              </ul>

              <DigitalConsentSummary />

              <div className="mt-9 flex flex-col items-stretch gap-3 sm:items-center">
                <Link
                  to="/youre-in"
                  className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--gradient-gold)] px-8 py-4 font-display text-[16px] font-medium text-foreground shadow-[var(--shadow-gold)] transition-all duration-300 hover:-translate-y-0.5 sm:w-auto sm:px-10 sm:text-[17px]"
                >
                  Continue to your welcome page
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
                </Link>
                <Link
                  to="/my-membership"
                  className="rounded-full px-5 py-2.5 text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                >
                  View my membership
                </Link>
              </div>
            </>
          )}

          {status === "pending" && (
            <>
              <h1 className="mt-5 font-display text-3xl leading-tight text-foreground sm:text-[38px]">
                Confirming your payment…
              </h1>
              <p className="mx-auto mt-4 max-w-lg text-[16px] leading-relaxed text-muted-foreground sm:text-[17px]">
                Your payment is being confirmed. You're welcome to stay on
                this page — it will update automatically the moment your
                Membership becomes active.
              </p>
              <p className="mx-auto mt-3 max-w-lg text-[15px] leading-relaxed text-foreground/80 sm:text-[16px]">
                It's also safe to close this page. We'll email you as soon
                as your membership is active, with a link straight into the
                Recovery Trauma™ platform.
              </p>
            </>
          )}

          {status === "timeout" && (
            <>
              <h1 className="mt-5 font-display text-3xl leading-tight text-foreground sm:text-[38px]">
                Payment received — activating your account
              </h1>
              <p className="mx-auto mt-4 max-w-lg text-[16px] leading-relaxed text-muted-foreground sm:text-[17px]">
                Your payment went through, but activation is taking a little
                longer than usual. Refresh in a moment, or open your
                membership page — it will show as active shortly.
              </p>
              <div className="mt-9 flex flex-col items-stretch gap-3 sm:items-center">
                <Link
                  to="/my-membership"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--gradient-gold)] px-8 py-4 font-display text-[16px] font-medium text-foreground shadow-[var(--shadow-gold)] transition-all duration-300 hover:-translate-y-0.5 sm:w-auto sm:px-10 sm:text-[17px]"
                >
                  Check my membership
                  <ArrowRight className="h-5 w-5" />
                </Link>
                <a
                  href="mailto:support@recoverytrauma.com"
                  className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                >
                  <Mail className="h-4 w-4" /> Contact support
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Bullet() {
  return (
    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--gradient-gold)] shadow-[var(--shadow-gold)]">
      <Check className="h-3 w-3 text-foreground" strokeWidth={3} />
    </span>
  );
}
